// /src/pages/About.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import seo from '../seo';

export default function About() {
  const { t } = useLanguage();
  const { theme } = useTheme();

  const pageTitle = t.aboutPage?.title || 'À propos';
  const description =
    t.aboutPage?.intro || t.footer?.description || 'MP3 Tag Editor, rapide et privé.';
  const url = seo.canonical('/about');

  // Cartes/blocs
  const card =
    theme === 'dark'
      ? 'bg-gray-800 border border-gray-700 text-gray-100'
      : 'bg-white border border-gray-200 text-gray-900';
  const muted = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const h2 = theme === 'dark'
    ? 'text-2xl font-semibold text-white'
    : 'text-2xl font-semibold text-gray-900';

  return (
    // Wrapper qui applique un fond gris pâle en light, et sombre en dark
    <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} transition-colors`}>
      <main className="max-w-5xl mx-auto px-4 py-10">
        <Helmet>
          <title>{pageTitle}</title>
          <link rel="canonical" href={url} />
          <meta name="description" content={description} />
          <meta name="robots" content="index,follow" />
          <script type="application/ld+json">
            {JSON.stringify(seo.webPage({ name: pageTitle, url, description }))}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(seo.org())}
          </script>
        </Helmet>

        <div className={`rounded-xl p-6 ${card}`}>
          <h1 className={theme === 'dark' ? 'text-3xl font-bold text-white' : 'text-3xl font-bold text-gray-900'}>
            {t.aboutPage?.title}
          </h1>
          <p className={`${muted} mt-3`}>{t.aboutPage?.intro}</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className={`rounded-lg p-5 ${card}`}>
              <h2 className={h2}>{t.aboutPage?.featuresTitle}</h2>
              <ul className="mt-3 list-disc pl-5 space-y-2">
                {(t.aboutPage?.features || []).map((line, i) => (
                  <li key={i} className={muted}>{line}</li>
                ))}
              </ul>
            </section>

            <section className={`rounded-lg p-5 ${card}`}>
              <h2 className={h2}>{t.aboutPage?.privacyTitle}</h2>
              <p className="mt-3">{t.aboutPage?.privacyBody}</p>
            </section>

            <section className={`rounded-lg p-5 ${card}`}>
              <h2 className={h2}>{t.aboutPage?.nameTitle}</h2>
              <p className="mt-3">{t.aboutPage?.nameBody1}</p>
              <p className="mt-3">{t.aboutPage?.nameBody2}</p>
              <div className="mt-3">
                <h3 className="font-medium mb-2">{t.aboutPage?.sourcesTitle}</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {(t.aboutPage?.nameSources || []).map((s, i) => (
                    <li key={i}>
                      <a
                        className="underline hover:no-underline"
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className={`rounded-lg p-5 ${card}`}>
              <h2 className={h2}>{t.aboutPage?.kracTitle}</h2>
              <p className="mt-3">{t.aboutPage?.kracBody}</p>
              <div className="mt-3">
                <ul className="list-disc pl-5 space-y-1">
                  {(t.aboutPage?.kracLinks || []).map((s, i) => (
                    <li key={i}>
                      <a
                        className="underline hover:no-underline"
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
